import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Row, Col, Image, Button, Spin, message, Typography, Tag, 
  Card, Space, Divider, Select, InputNumber, Tooltip 
} from 'antd';
import { 
  LeftOutlined, RightOutlined, ArrowLeftOutlined, 
  ShoppingCartOutlined, CheckCircleFilled, ExclamationCircleFilled 
} from '@ant-design/icons';
import { productAPI, dropshipperAPI } from '../api';
import countryList from 'react-select-country-list';
import { normalizeMoney } from '../utils/money';
import Header from '../components/Header';
import Footer from '../components/Footer';
import './ProductDetailPage.css';

const { Title, Paragraph, Text } = Typography;
const { Option } = Select;

// 获取API基础URL
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

// 添加CSS样式用于产品描述中的表格
// 使用较低优先级的选择器，避免覆盖表格自身的样式
const productDescriptionStyles = `
  .product-description table:not([style*="border"]) {
    border-collapse: collapse;
    width: 100%;
    margin: 16px 0;
    border: 1px solid #d9d9d9;
  }
  
  .product-description table th:not([style*="border"]),
  .product-description table td:not([style*="border"]) {
    border: 1px solid #d9d9d9;
    padding: 8px 12px;
    text-align: left;
  }
  
  .product-description table th:not([style*="background"]) {
    background-color: #f5f5f5;
    font-weight: 600;
  }
  
  .product-description table tr:nth-child(even):not([style*="background"]) {
    background-color: #fafafa;
  }
  
  
  /* 为没有样式的表格添加基础样式 */
  .product-description table:not([style]) {
    border-collapse: collapse;
    width: 100%;
    margin: 16px 0;
    border: 1px solid #d9d9d9;
  }
  
  .product-description table:not([style]) th,
  .product-description table:not([style]) td {
    border: 1px solid #d9d9d9;
    padding: 8px 12px;
    text-align: left;
  }
  
  .product-description table:not([style]) th {
    background-color: #f5f5f5;
    font-weight: 600;
  }
  
  .product-description table:not([style]) tr:nth-child(even) {
    background-color: #fafafa;
  }
  
`;

// 将样式添加到页面
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = productDescriptionStyles;
  document.head.appendChild(styleElement);
}

const ProductDetailPage = () => {
  const { product_id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedVariantPath, setSelectedVariantPath] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [userSecondLevelChoice, setUserSecondLevelChoice] = useState(''); // 记录用户在第二层级的选择
  const [addingProduct, setAddingProduct] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [hoveredVariantImage, setHoveredVariantImage] = useState(null); // 存储鼠标悬停的变体图片
  const [freightChannels, setFreightChannels] = useState([]);
  const [exchangeRates, setExchangeRates] = useState({});
  const [selectedCountry, setSelectedCountry] = useState('US');
  const [selectedFreightId, setSelectedFreightId] = useState(undefined);
  const originalTitleRef = useRef('');
  const hasUserSelectedCountryRef = useRef(false);
  const productSubheader = (product?.subheader || '').trim();
  const displayProductName = product?.name
    ? `${product.name}${productSubheader ? ` ${productSubheader}` : ''}`
    : '';
  const countryOptions = useMemo(
    () =>
      countryList().getData().map(country => ({
        value: country.value,
        label: country.label
      })),
    []
  );
  const availableFreightChannels = useMemo(() => {
    if (!Array.isArray(freightChannels) || !freightChannels.length) {
      return [];
    }

    const supportedIds = product?.supported_freight_channels || [];
    if (!supportedIds.length) {
      return freightChannels;
    }

    return freightChannels.filter((channel) => supportedIds.includes(channel.id));
  }, [product, freightChannels]);

  const normalizeVariantPath = (path) => {
    if (Array.isArray(path)) {
      return [...path];
    }

    if (typeof path === 'string') {
      const trimmed = path.trim();
      if (!trimmed) {
        return [];
      }

      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      } catch (error) {
        // ignore JSON parse errors and fall back to legacy dot-separated format
      }

      return trimmed.split('.').filter(part => part.length > 0);
    }

    return [];
  };
  
  const compareVariantPaths = (pathA = [], pathB = []) => {
    const maxLength = Math.max(pathA.length, pathB.length);
    for (let i = 0; i < maxLength; i++) {
      const valueA = pathA[i] ?? '';
      const valueB = pathB[i] ?? '';
      const comparison = String(valueA).localeCompare(
        String(valueB),
        undefined,
        { numeric: true, sensitivity: 'base' }
      );
      if (comparison !== 0) {
        return comparison;
      }
    }
    return pathA.length - pathB.length;
  };
  
  const getSortedVariants = (variants) => {
    if (!Array.isArray(variants)) return [];
    return [...variants].sort((variantA, variantB) => {
      const pathA = normalizeVariantPath(variantA.variant_path);
      const pathB = normalizeVariantPath(variantB.variant_path);
      return compareVariantPaths(pathA, pathB);
    });
  };

  useEffect(() => {
    fetchProduct();
  }, [product_id]);

  useEffect(() => {
    fetchShippingMeta();
  }, []);

  useEffect(() => {
    let isCancelled = false;

    const detectCountryByIP = async () => {
      try {
        const response = await fetch('https://ipinfo.io/json');
        if (!response.ok) return;

        const data = await response.json();
        const detectedCountry = typeof data?.country === 'string'
          ? data.country.trim().toUpperCase()
          : '';

        if (!detectedCountry) return;

        const isSupportedCountry = countryOptions.some(
          option => option.value === detectedCountry
        );

        if (!isSupportedCountry || isCancelled || hasUserSelectedCountryRef.current) return;
        setSelectedCountry(detectedCountry);
      } catch (error) {
        // Ignore IP lookup failures and keep the default country.
      }
    };

    detectCountryByIP();

    return () => {
      isCancelled = true;
    };
  }, [countryOptions]);

  const handleCountryChange = (countryCode) => {
    hasUserSelectedCountryRef.current = true;
    setSelectedCountry(countryCode);
  };

  useEffect(() => {
    if (!availableFreightChannels.length) {
      if (selectedFreightId !== undefined) {
        setSelectedFreightId(undefined);
      }
      return;
    }

    const hasSelectedChannel = availableFreightChannels.some(
      (channel) => channel.id === selectedFreightId
    );
    if (!hasSelectedChannel) {
      setSelectedFreightId(availableFreightChannels[0].id);
    }
  }, [availableFreightChannels, selectedFreightId]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    originalTitleRef.current = document.title;

    return () => {
      document.title = originalTitleRef.current;
    };
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const originalTitle = originalTitleRef.current || document.title;

    document.title = displayProductName
      ? `${displayProductName} - ${originalTitle}`
      : originalTitle;
  }, [displayProductName]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await productAPI.getById(product_id, { include_variant_images: true });
      if (Array.isArray(response.data.variants)) {
        response.data.variants = getSortedVariants(response.data.variants);
      }
      setProduct(response.data);
      setCurrentImageIndex(0);
      
      // 初始化默认变体 - 适配新的扁平化格式
      if (response.data.variants && Array.isArray(response.data.variants) && response.data.variants.length > 0) {
        // 选择排序后的第一个变体
        const sortedVariants = getSortedVariants(response.data.variants);
        const firstVariant = sortedVariants[0];
        setSelectedVariant(firstVariant);
        setSelectedVariantPath(
          firstVariant.variant_path ? normalizeVariantPath(firstVariant.variant_path) : []
        );
        
        // 初始化第二级选择记录（如果有的话）
        if (firstVariant.variant_path && firstVariant.variant_path.length >= 2) {
          setUserSecondLevelChoice(firstVariant.variant_path[1]); // 记录初始的第二级选择
        }
      }
    } catch (error) {
      
      messageApi.error('Failed to load product details');
      // 如果产品不存在，返回首页
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const fetchShippingMeta = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const [freightResult, rateResult] = await Promise.allSettled([
        fetch(`${API_BASE_URL}/dropshipper/freight-channels`, { headers }),
        fetch(`${API_BASE_URL}/exchange-rates`, { headers })
      ]);

      if (freightResult.status === 'fulfilled' && freightResult.value.ok) {
        const freightData = await freightResult.value.json();
        if (Array.isArray(freightData)) {
          setFreightChannels(freightData);
        }
      }

      if (rateResult.status === 'fulfilled' && rateResult.value.ok) {
        const rateData = await rateResult.value.json();
        if (Array.isArray(rateData)) {
          const rates = {};
          rateData.forEach(rate => {
            rates[rate.currency_code] = rate.rate_to_usd;
          });
          rates.USD = 1.0;
          setExchangeRates(rates);
        }
      }
    } catch (error) {
      // Shipping estimator is optional on detail page, ignore fetch failures silently.
    }
  };

  const toNumber = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const num = Number(value);
    return Number.isNaN(num) ? 0 : num;
  };

  const convertToUSD = (amount, currency) => {
    const numericAmount = toNumber(amount);
    if (!currency || currency.toUpperCase() === 'USD') {
      return numericAmount;
    }

    const rate = toNumber(exchangeRates[currency.toUpperCase()]);
    if (!rate) {
      return numericAmount;
    }

    return numericAmount / rate;
  };

  const calculateFreightCost = (freight, destCountry, weight) => {
    if (!freight?.data || !destCountry) {
      return { cost: 0, error: 'Shipping data unavailable' };
    }

    const countryData = freight.data[destCountry];
    if (!countryData) {
      const countryName = countryOptions.find(c => c.value === destCountry)?.label || destCountry;
      return { cost: 0, error: `Cannot ship to ${countryName} with ${freight.name}` };
    }

    if (!countryData.weight) {
      return { cost: 0, error: 'No weight configuration found' };
    }

    let suitableRange = null;
    for (const [weightStr, pricing] of Object.entries(countryData.weight)) {
      const weightLimit = parseInt(weightStr, 10);
      if (weight <= weightLimit) {
        if (!suitableRange || weightLimit < parseInt(suitableRange[0], 10)) {
          suitableRange = [weightStr, pricing];
        }
      }
    }

    if (!suitableRange) {
      const maxWeight = Math.max(...Object.keys(countryData.weight).map(w => parseInt(w, 10)));
      return { cost: 0, error: `Weight (${weight}g) exceeds maximum (${maxWeight}g)` };
    }

    const pricing = suitableRange[1];
    const prePacket = parseFloat(pricing.pre_packet || 0);
    const preKg = parseFloat(pricing.pre_kg || 0);
    const currency = pricing.currency || 'USD';
    const weightKg = weight / 1000.0;
    const originalFreightPrice = prePacket + (preKg * weightKg);

    return {
      cost: convertToUSD(originalFreightPrice, currency),
      error: null
    };
  };

  const getShippingPreview = () => {
    if (!selectedVariant || !availableFreightChannels.length || !selectedFreightId) {
      return { cost: 0, error: 'Shipping data unavailable' };
    }

    const selectedFreight = availableFreightChannels.find(channel => channel.id === selectedFreightId);
    if (!selectedFreight) {
      return { cost: 0, error: 'Shipping data unavailable' };
    }

    const weight = toNumber(selectedVariant.weight || 100);
    return calculateFreightCost(selectedFreight, selectedCountry, weight);
  };

  const handleImageChange = (direction) => {
    const availableImages = getCurrentVariantImages();
    if (!availableImages.length) return;
    
    const maxIndex = availableImages.length - 1;
    if (direction === 'prev') {
      setCurrentImageIndex(prev => prev > 0 ? prev - 1 : maxIndex);
    } else {
      setCurrentImageIndex(prev => prev < maxIndex ? prev + 1 : 0);
    }
  };

  // 左上角主图/缩略图仅展示商品通用图（非变体图）
  const getCurrentVariantImages = () => {
    if (!product?.images?.length) return [];
    return product.images.filter(img => !img.for_variants);
  };

  const getProductImage = (imageIndex = currentImageIndex) => {
    const availableImages = getCurrentVariantImages();
    
    if (!availableImages.length) {
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTAwIiBoZWlnaHQ9IjUwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtc2l6ZT0iMjQiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5ObyBJbWFnZTwvdGV4dD48L3N2Zz4=';
    }
    
    const imageData = availableImages[imageIndex];
    return `${API_BASE_URL}/products/${product.id}/images/${imageData.id}`;
  };


  // 获取所有变体选项 - 适配新的扁平化格式
  const getVariantOptions = (variants) => {
    if (!variants || !Array.isArray(variants)) return [];
    
    return variants.map((variant, index) => {
      const pathArray = normalizeVariantPath(variant.variant_path);
      return {
        value: JSON.stringify(pathArray),
        label: pathArray.length > 0
          ? pathArray.join(' - ')
          : `Variant ${index + 1}`,
        data: variant
      };
    });
  };

  // 处理变体选择 - 适配新的扁平化格式
  const handleVariantChange = (variantPath) => {
    if (!product.variants || !Array.isArray(product.variants)) return;
    
    // 将路径转换为数组格式进行比较
    const pathArray = normalizeVariantPath(variantPath);
    
    // 查找匹配的变体
    const selectedVariant = product.variants.find(variant => {
      if (!variant.variant_path || variant.variant_path.length !== pathArray.length) {
        return false;
      }
      return variant.variant_path.every((value, index) => value === pathArray[index]);
    });
    
    if (selectedVariant) {
      setSelectedVariant(selectedVariant);
      setSelectedVariantPath([...pathArray]);
      setCurrentImageIndex(0); // 重置图片索引
    }
  };

  // 解析变体层级结构 - 适配新的扁平化格式
  const parseVariantLevels = (variants, variantTypes) => {
    
    console.log('🔍 variants:', JSON.stringify(variants, null, 2));
    
    
    if (!variants || !Array.isArray(variants) || variants.length === 0) {
      
      return [];
    }

    if (!variantTypes || !Array.isArray(variantTypes) || variantTypes.length === 0) {
      
      return [];
    }

    const levels = [];
    
    // 遍历每个变体类型，收集所有可能的值
    for (let typeIndex = 0; typeIndex < variantTypes.length; typeIndex++) {
      const typeName = variantTypes[typeIndex];
      const values = new Set();
      
      // 遍历所有变体，收集该类型的所有值
      variants.forEach(variant => {
        if (variant.variant_path && variant.variant_path[typeIndex] !== undefined) {
          values.add(variant.variant_path[typeIndex]);
        }
      });
      
      const sortedValues = Array.from(values).sort((a, b) => 
        String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' })
      );
      
      levels.push({
        name: typeName,
        values: sortedValues
      });
      
      console.log(`✅ Added level ${typeIndex}: ${typeName} with values:`, Array.from(values));
    }

    
    return levels;
  };



  // 渲染变体选择器 - 适配新的扁平化格式
  const renderVariantSelector = () => {
    
    console.log('🎨 product.variants:', JSON.stringify(product.variants, null, 2));
    
    
    const levels = parseVariantLevels(product.variants, product.variant_types);
    
    
    if (levels.length === 0) {
      
      return null;
    }

    
    return (
      <div>
        {levels.map((level, levelIndex) => (
          <div key={levelIndex} style={{ marginBottom: '16px' }}>
            <Text strong style={{ display: 'block', marginBottom: '8px', color: '#666' }}>
              {level.name}:
            </Text>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {level.values.map((value) => {
                const isSelected = isVariantValueSelected(value, levelIndex);
                const isAvailable = isVariantValueAvailable(value, levelIndex);
                const variantImageId = getHoveredVariantImage(value, levelIndex);
                
                const buttonElement = (
                  <Button
                    key={value}
                    type={isSelected ? 'primary' : 'default'}
                    style={{
                      minWidth: '60px',
                      height: '36px',
                      borderRadius: '6px',
                      border: isSelected ? '2px solid #2557ff' : '1px solid #d9d9d9',
                      backgroundColor: isSelected ? '#2557ff' : isAvailable ? '#fff' : '#f5f5f5',
                      color: isSelected ? '#fff' : isAvailable ? '#333' : '#bbb',
                      cursor: isAvailable ? 'pointer' : 'not-allowed',
                      opacity: isAvailable ? 1 : 0.5,
                      pointerEvents: isAvailable ? 'auto' : 'none'
                    }}
                    disabled={!isAvailable}
                    title={!isAvailable ? 'This combination is not available' : ''}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (isAvailable) {
                         // 调试日志
                        handleVariantValueClick(value, levelIndex);
                      }
                    }}
                    onMouseEnter={() => {
                      if (variantImageId) {
                        setHoveredVariantImage(variantImageId);
                      }
                    }}
                    onMouseLeave={() => {
                      setHoveredVariantImage(null);
                    }}
                  >
                    {value}
                  </Button>
                );

                // 如果有图片，用Tooltip包裹按钮
                if (variantImageId && isAvailable) {
                  return (
                    <Tooltip
                      key={value}
                      title={
                        <img
                          src={`${API_BASE_URL}/products/${product.id}/images/${variantImageId}`}
                          alt={value}
                          style={{
                            width: '64px',
                            height: '64px',
                            objectFit: 'cover',
                            borderRadius: '4px'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      }
                      overlayInnerStyle={{
                        padding: '4px',
                        backgroundColor: 'white',
                        border: '1px solid #d9d9d9',
                        borderRadius: '6px'
                      }}
                      mouseEnterDelay={0}
                      mouseLeaveDelay={0}
                      transitionName=""
                    >
                      {buttonElement}
                    </Tooltip>
                  );
                }

                // 没有图片时直接返回按钮
                return buttonElement;
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // 检查变体值是否被选中 - 适配新的扁平化格式
  const isVariantValueSelected = (value, levelIndex) => {
    const pathParts = normalizeVariantPath(selectedVariantPath);
    if (!pathParts.length) return false;
    
    
    
    
    // 新的扁平化结构：直接按索引对应
    const isSelected = pathParts[levelIndex] === value;
    
    
    return isSelected;
  };

  // 检查变体值是否可用 - 适配新的扁平化格式
  const isVariantValueAvailable = (value, levelIndex) => {
    if (!product.variants || !Array.isArray(product.variants)) return false;
    
    // 获取当前已选择的值
    const currentPath = normalizeVariantPath(selectedVariantPath);
    
    // 构建测试路径：将当前值插入到指定位置
    const testPath = [...currentPath];
    testPath[levelIndex] = value;
    
    // 检查是否存在匹配的变体
    // 关键修复：只检查到当前级别为止的匹配，不考虑后续级别的选择
    // 这样可以避免下一级的选择限制上一级的可选项
    const hasMatchingVariant = product.variants.some(variant => {
      if (!variant.variant_path) return false;
      
      // 只检查到当前级别为止的所有位置是否匹配
      // 不检查后续级别，因为用户可以在选择当前级别后重新选择后续级别
      for (let i = 0; i <= levelIndex; i++) {
        if (variant.variant_path[i] !== testPath[i]) {
          return false;
        }
      }
      
      return true;
    });
    
    
    console.log(`🔍 Test path (up to current level):`, testPath.slice(0, levelIndex + 1));
    
    return hasMatchingVariant;
  };

  // 获取鼠标悬停时的变体图片
  const getHoveredVariantImage = (value, levelIndex) => {
    if (!product.variants || !Array.isArray(product.variants)) return null;
    
    // 获取当前已选择的值
    const currentPath = normalizeVariantPath(selectedVariantPath);
    
    // 构建测试路径：将当前值插入到指定位置
    const testPath = [...currentPath];
    testPath[levelIndex] = value;
    
    // 查找匹配的变体（优先找完全匹配，如果没有则找部分匹配）
    let matchedVariant = null;
    
    // 首先尝试找完全匹配的变体
    matchedVariant = product.variants.find(variant => {
      if (!variant.variant_path || variant.variant_path.length !== testPath.length) {
        return false;
      }
      return variant.variant_path.every((val, index) => val === testPath[index]);
    });
    
    // 如果没有完全匹配，找部分匹配（匹配到当前级别）
    if (!matchedVariant) {
      matchedVariant = product.variants.find(variant => {
        if (!variant.variant_path) return false;
        
        for (let i = 0; i <= levelIndex; i++) {
          if (variant.variant_path[i] !== testPath[i]) {
            return false;
          }
        }
        
        return true;
      });
    }
    
    // 返回变体的图片ID
    return matchedVariant && matchedVariant.image ? matchedVariant.image : null;
  };


  // 处理变体值点击 - 适配新的扁平化格式
  const handleVariantValueClick = (value, levelIndex) => {
    if (!product.variants || !Array.isArray(product.variants)) return;
    
    
    
    // 获取当前已选择的值
    const currentPath = normalizeVariantPath(selectedVariantPath);
    
    // 构建新的路径，保持其他级别的选择不变
    const newPath = [...currentPath];
    newPath[levelIndex] = value;
    
    
    
    
    // 检查当前路径是否有效（是否存在完全匹配的变体）
    let exactMatchVariant = product.variants.find(variant => {
      if (!variant.variant_path || variant.variant_path.length !== newPath.length) {
        return false;
      }
      return variant.variant_path.every((val, index) => val === newPath[index]);
    });
    
    if (exactMatchVariant) {
      // 如果存在完全匹配的变体，直接使用
      
      const newVariantPath = normalizeVariantPath(exactMatchVariant.variant_path);
      handleVariantChange(newVariantPath);
      return;
    }
    
    // 如果没有完全匹配，需要处理后续级别的选择
    
    
    // 验证并调整后续级别的选择
    const adjustedPath = [...newPath];
    
    // 从当前修改的级别的下一级开始检查
    for (let i = levelIndex + 1; i < adjustedPath.length; i++) {
      if (!adjustedPath[i]) continue; // 如果该级别没有选择，跳过
      
      // 检查当前路径到第i级是否有可用的变体
      const testPath = adjustedPath.slice(0, i + 1);
      const hasValidVariant = product.variants.some(variant => {
        if (!variant.variant_path || variant.variant_path.length < testPath.length) {
          return false;
        }
        return testPath.every((val, index) => variant.variant_path[index] === val);
      });
      
      if (!hasValidVariant) {
        
        // 如果当前级别的选择无效，清空该级别及后续级别的选择
        for (let j = i; j < adjustedPath.length; j++) {
          adjustedPath[j] = '';
        }
        break;
      }
    }
    
    
    
    // 尝试找到最佳匹配的变体
    let bestMatchVariant = null;
    
    // 首先尝试找到完全匹配调整后路径的变体
    bestMatchVariant = product.variants.find(variant => {
      if (!variant.variant_path || variant.variant_path.length !== adjustedPath.length) {
        return false;
      }
      return variant.variant_path.every((val, index) => val === adjustedPath[index]);
    });
    
    // 如果没有完全匹配，找到部分匹配的变体并自动选择后续级别
    if (!bestMatchVariant) {
      
      
      // 找到所有匹配已选择部分的变体
      const partialMatches = product.variants.filter(variant => {
        if (!variant.variant_path) return false;
        
        // 检查所有非空的已选择位置是否匹配
        for (let i = 0; i < adjustedPath.length; i++) {
          if (adjustedPath[i] && variant.variant_path[i] !== adjustedPath[i]) {
            return false;
          }
        }
        return true;
      });
      
      if (partialMatches.length > 0) {
        // 选择排序后的第一个匹配变体
        partialMatches.sort((a, b) => 
          compareVariantPaths(
            normalizeVariantPath(a.variant_path),
            normalizeVariantPath(b.variant_path)
          )
        );
        bestMatchVariant = partialMatches[0];
        
      }
    }
    
    if (bestMatchVariant) {
      const newVariantPath = normalizeVariantPath(bestMatchVariant.variant_path);
      
      handleVariantChange(newVariantPath);
      
      // 记录第二级选择（如果有的话）
      if (levelIndex === 1 && bestMatchVariant.variant_path.length > 1) {
        setUserSecondLevelChoice(bestMatchVariant.variant_path[1]);
      }
    } else {
      
    }
  };

  const handleAddToStore = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      messageApi.info('Please login to add product to My Products');
      navigate('/login');
      return;
    }

    try {
      setAddingProduct(true);
      await dropshipperAPI.addProduct({
        product_id: product.id,
      });

      messageApi.success({
        icon: <CheckCircleFilled style={{ color: '#52c41a' }} />,
        content: (
          <span>
            Product has been added to{' '}
            <a
              href="/dropshipper/my-products"
              onClick={(e) => {
                e.preventDefault();
                navigate('/dropshipper/my-products');
              }}
              style={{ fontWeight: 600 }}
            >
              My Products
            </a>
          </span>
        ),
        duration: 3,
      });
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to add product';
      if (detail === 'This product is already in My Products') {
        messageApi.open({
          type: 'warning',
          icon: <ExclamationCircleFilled style={{ color: '#faad14' }} />,
          content: (
            <span>
              This product is already in{' '}
              <a
                href="/dropshipper/my-products"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/dropshipper/my-products');
                }}
                style={{ fontWeight: 600 }}
              >
                My Products
              </a>
            </span>
          ),
          duration: 3,
        });
      } else {
        messageApi.error(detail);
      }
    } finally {
      setAddingProduct(false);
    }
  };

  const handleOrderSamples = () => {
    // 检查用户是否已登录
    const token = localStorage.getItem('token');
    
    if (!token) {
      // 未登录，保存产品信息并跳转到登录页
      const productData = {
        product_id: product.id,
        product_name: product.name,
        variant_path: selectedVariantPath,
        variant_data: selectedVariant,
        action: 'order_samples',
        redirect_url: '/dropshipper/create-order'
      };
      
      localStorage.setItem('pending_action', JSON.stringify(productData));
      messageApi.info('Please login to order samples');
      navigate('/login');
      return;
    }
    
    // 已登录，直接跳转并传递产品信息
    const productData = {
      product_id: product.id,
      product_name: product.name,
      variant_path: selectedVariantPath,
      variant_data: selectedVariant
    };
    
    localStorage.setItem('selected_product_for_order', JSON.stringify(productData));
    navigate('/dropshipper/create-order');
  };

  if (loading) {
    return (
      <>
        {contextHolder}
        <div style={{ 
          minHeight: '100vh', 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          background: 'radial-gradient(circle at 10% 10%, rgba(210, 226, 255, 0.6), transparent 50%), radial-gradient(circle at 90% 0%, rgba(179, 205, 255, 0.35), transparent 55%), linear-gradient(180deg, #fafdff 0%, #eef2ff 100%)'
        }}>
          <Spin size="large" />
        </div>
      </>
    );
  }

  if (!product) {
    return (
      <>
        {contextHolder}
        <div style={{ 
          minHeight: '100vh', 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          background: 'radial-gradient(circle at 10% 10%, rgba(210, 226, 255, 0.6), transparent 50%), radial-gradient(circle at 90% 0%, rgba(179, 205, 255, 0.35), transparent 55%), linear-gradient(180deg, #fafdff 0%, #eef2ff 100%)'
        }}>
          <div style={{ textAlign: 'center' }}>
            <Title level={3}>Product not found</Title>
            <Button type="primary" onClick={() => navigate('/')}>
              Back to Home
            </Button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {contextHolder}
      <div className="product-detail-page shared-background">
        {/* Header */}
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 24px' }}>
          <Header />
        </div>

        {/* Main Content */}
      <div className="product-detail-content">
        <Row gutter={[40, 40]}>
          {/* 左侧图片区域 */}
          <Col xs={24} md={14}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

              {/* 商品图片浏览器 */}
              <Card 
                style={{ 
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                  backgroundColor: 'rgba(255, 255, 255, 0.5)'
                }}
                bodyStyle={{ padding: 0 }}
              >
                {/* 主图片显示 */}
                <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <Image
                    src={getProductImage()}
                    alt={product.name}
                    preview={{
                      mask: null
                    }}
                    style={{
                      width: 'auto',
                      maxWidth: '100%',
                      height: selectedVariant && selectedVariant.image ? '300px' : '400px',
                      objectFit: 'contain'
                    }}
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTAwIiBoZWlnaHQ9IjQwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtc2l6ZT0iMjQiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5Qcm9kdWN0IEltYWdlPC90ZXh0Pjwvc3ZnPg==';
                    }}
                  />

                  {/* 左右切换按钮 */}
                  {(() => {
                    const availableImages = getCurrentVariantImages();
                    return availableImages.length > 1 && (
                      <>
                        <Button
                          type="primary"
                          shape="circle"
                          icon={<LeftOutlined />}
                          size="large"
                          style={{
                            position: 'absolute',
                            left: '20px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            backgroundColor: 'rgba(0,0,0,0.6)',
                            borderColor: 'transparent',
                            zIndex: 2
                          }}
                          onClick={() => handleImageChange('prev')}
                        />
                        <Button
                          type="primary"
                          shape="circle"
                          icon={<RightOutlined />}
                          size="large"
                          style={{
                            position: 'absolute',
                            right: '20px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            backgroundColor: 'rgba(0,0,0,0.6)',
                            borderColor: 'transparent',
                            zIndex: 2
                          }}
                          onClick={() => handleImageChange('next')}
                        />
                      </>
                    );
                  })()}

                  {/* 图片索引指示器 */}
                  {(() => {
                    const availableImages = getCurrentVariantImages();
                    return availableImages.length > 1 && (
                      <div style={{
                        position: 'absolute',
                        bottom: '16px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: 'rgba(0,0,0,0.6)',
                        color: 'white',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px'
                      }}>
                        {currentImageIndex + 1} / {availableImages.length}
                      </div>
                    );
                  })()}
                </div>

                {/* 缩略图导航 */}
                {(() => {
                  const availableImages = getCurrentVariantImages();
                  return availableImages.length > 1 && (
                    <div style={{ 
                      padding: '16px',
                      display: 'flex',
                      justifyContent: 'center',
                      gap: '8px', 
                      overflowX: 'auto',
                      scrollbarWidth: 'thin'
                    }}>
                      {availableImages.map((image, index) => (
                        <div
                          key={image.id}
                          style={{
                            minWidth: '60px',
                            width: '60px',
                            height: '60px',
                            cursor: 'pointer',
                            border: index === currentImageIndex ? '2px solid #2557ff' : '2px solid transparent',
                            borderRadius: '6px',
                            overflow: 'hidden',
                            transition: 'all 0.3s ease'
                          }}
                          onClick={() => setCurrentImageIndex(index)}
                        >
                          <img
                            src={`${API_BASE_URL}/products/${product.id}/images/${image.id}`}
                            alt={`Thumbnail ${index + 1}`}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              opacity: index === currentImageIndex ? 1 : 0.7,
                              transition: 'opacity 0.3s ease'
                            }}
                            onError={(e) => {
                              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LXNpemU9IjgiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5OL0E8L3RleHQ+PC9zdmc+';
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </Card>

              {/* 商品描述 - 移到左侧面板 */}
              <Card 
                style={{ 
                  borderRadius: '12px',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                  backgroundColor: 'rgba(255, 255, 255, 0.5)'
                }}
              >
                <Title level={4} style={{ color: '#333', marginBottom: '12px' }}>
                  Product Description
                </Title>
                <div 
                  style={{ 
                    fontSize: '16px', 
                    lineHeight: '1.6',
                    color: '#666'
                  }}
                  dangerouslySetInnerHTML={{ 
                    __html: product.description || 'No description available for this product.' 
                  }}
                  className="product-description"
                />
                
                {/* 商品描述图片 */}
                {product.description_images && product.description_images.length > 0 && (
                  <div style={{ marginTop: '24px' }}>
                    <Divider />
                    {product.description_images.map((descImage, index) => (
                      <div key={descImage.id} style={{ marginBottom: '16px' }}>
                        <Image
                          src={`${API_BASE_URL}/products/${product.id}/description_images/${descImage.id}`}
                          alt={`Description ${index + 1}`}
                          preview={{
                            mask: null
                          }}
                          style={{
                            width: '100%',
                            borderRadius: '8px'
                          }}
                          onError={(e) => {
                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTAwIiBoZWlnaHQ9IjQwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtc2l6ZT0iMjQiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5EZXNjcmlwdGlvbiBJbWFnZTwvdGV4dD48L3N2Zz4=';
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          </Col>

          {/* 右侧商品信息区域 */}
          <Col xs={24} md={10}>
            <div style={{ height: '100%' }}>
              {/* 商品标题 */}
              <Title level={1} style={{ 
                color: '#333', 
                marginBottom: '16px',
                fontSize: '32px',
                lineHeight: '1.2'
              }}>
                {displayProductName}
              </Title>

              {/* 分类标签 */}
              {product.category && (
                <div style={{ marginBottom: '20px' }}>
                  <Tag 
                    color="#2557ff" 
                    style={{ 
                      fontSize: '14px', 
                      padding: '4px 12px',
                      borderRadius: '6px'
                    }}
                  >
                    {product.category.name}
                  </Tag>
                </div>
              )}

              {/* 价格 */}
              <div style={{ marginBottom: '24px' }}>
                <Text style={{ 
                  fontSize: '36px', 
                  fontWeight: 'bold', 
                  color: '#2557ff'
                }}>
                  {selectedVariant ? 
                    `${selectedVariant.currency || 'USD'} ${(selectedVariant.price || 0).toFixed(2)}` :
                    'N/A'
                  }
                </Text>
                {selectedVariant?.SKU && (
                  <div style={{ marginTop: '8px' }}>
                    <Text type="secondary">SKU: {selectedVariant.SKU}</Text>
                  </div>
                )}
                {(() => {
                  const shippingPreview = getShippingPreview();
                  const isCannotShipError = Boolean(
                    shippingPreview.error &&
                    shippingPreview.error.startsWith('Cannot ship to')
                  );

                  if (!availableFreightChannels.length) {
                    return null;
                  }

                  return (
                    <div style={{ marginTop: '12px' }}>
                      <Space size={6} wrap align="center">
                        <Text type="secondary">Ship to</Text>
                        <Select
                          value={selectedCountry}
                          onChange={handleCountryChange}
                          size="small"
                          variant="borderless"
                          showSearch
                          optionFilterProp="label"
                          style={{
                            minWidth: 120,
                            maxWidth: 170,
                            fontSize: 14
                          }}
                          dropdownMatchSelectWidth={220}
                          options={countryOptions}
                        />
                        <Text type="secondary">via</Text>
                        <Select
                          value={selectedFreightId}
                          onChange={setSelectedFreightId}
                          size="small"
                          variant="borderless"
                          style={{
                            minWidth: 110,
                            maxWidth: 180,
                            fontSize: 14
                          }}
                          dropdownMatchSelectWidth={240}
                          options={availableFreightChannels.map((channel) => ({
                            value: channel.id,
                            label: channel.name
                          }))}
                        />
                        {!isCannotShipError && (
                          <>
                            <Text type="secondary">:</Text>
                            <Text
                              strong
                              style={{ color: shippingPreview.error ? '#ff4d4f' : '#333' }}
                            >
                              {shippingPreview.error
                                ? 'Unavailable'
                                : `$${normalizeMoney(shippingPreview.cost)} USD`}
                            </Text>
                          </>
                        )}
                      </Space>
                      {shippingPreview.error && (
                        <div style={{ marginTop: '6px' }}>
                          <Text type="danger" style={{ fontSize: '12px' }}>
                            {shippingPreview.error}
                          </Text>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* 变体选择 - 适配新的扁平化格式 */}
              {(() => {
                
                
                
                
                if (!product.variants || !Array.isArray(product.variants) || product.variants.length === 0) {
                  
                  return null;
                }
                
                if (!product.variant_types || !Array.isArray(product.variant_types) || product.variant_types.length === 0) {
                  
                  return null;
                }
                
                const levels = parseVariantLevels(product.variants, product.variant_types);
                
                
                if (levels.length === 0) {
                  
                  return null;
                }
                
                
                return (
                  <div style={{ marginBottom: '24px' }}>
                    {renderVariantSelector()}
                    
                    {/* 当前选中规格的图片展示 - 移到变体选择器下面 */}
                    {selectedVariant && selectedVariant.image && (
                      <Card 
                        style={{ 
                          borderRadius: '12px',
                          overflow: 'hidden',
                          boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                          marginTop: '16px',
                          backgroundColor: 'rgba(255, 255, 255, 0.5)'
                        }}
                        bodyStyle={{ padding: 0 }}
                      >
                        <div style={{ position: 'relative', display: 'flex', justifyContent: 'flex-end' }}>
                          <div style={{ position: 'relative', width: '70%' }}>
                            <Image
                              src={`${API_BASE_URL}/products/${product.id}/images/${selectedVariant.image}`}
                              alt={`Selected variant image`}
                              preview={{
                                mask: null
                              }}
                              style={{
                                width: '100%',
                                height: '200px',
                                objectFit: 'cover',
                                borderRadius: '0 12px 12px 0'
                              }}
                              onError={(e) => {
                                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtc2l6ZT0iMTgiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5WYXJpYW50IEltYWdlPC90ZXh0Pjwvc3ZnPg==';
                              }}
                            />
                          </div>
                          {/* 规格标识 - 移到左侧空白区域 */}
                          <div style={{
                            position: 'absolute',
                            top: '12px',
                            left: '12px',
                            backgroundColor: 'rgba(37, 87, 255, 0.9)',
                            color: 'white',
                            padding: '8px 16px',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            maxWidth: '25%',
                            wordBreak: 'break-word',
                            lineHeight: '1.3'
                          }}>
                            {(() => {
                              if (selectedVariant && selectedVariant.variant_path && product.variant_types) {
                                const pathParts = selectedVariant.variant_path;
                                return pathParts.map((value, index) => (
                                  <div key={index}>
                                    {product.variant_types[index]}: {value}
                                  </div>
                                ));
                              }
                              return 'Selected Variant';
                            })()}
                          </div>
                        </div>
                      </Card>
                    )}
                  </div>
                );
              })()}

              <Divider />

              {/* 库存信息 */}
              {selectedVariant && selectedVariant.stock !== -1 && (
                <div style={{ marginBottom: '24px' }}>
                  <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                    Stock:
                  </Text>
                  <Text style={{
                    color: selectedVariant && selectedVariant.stock > 0 ? '#52c41a' : '#ff4d4f',
                    fontWeight: 'bold',
                    fontSize: '16px'
                  }}>
                    {selectedVariant ? (
                      selectedVariant.stock > 0 ?
                        `${selectedVariant.stock} units available` :
                        'Out of stock'
                    ) : 'Please select variant'}
                  </Text>
                </div>
              )}

              {/* Action Buttons */}
              <Row gutter={12}>
                <Col span={14}>
                  <Button
                    type="primary"
                    size="large"
                    icon={<ShoppingCartOutlined />}
                    onClick={handleAddToStore}
                    loading={addingProduct}
                    disabled={
                      addingProduct ||
                      !selectedVariant ||
                      (selectedVariant.stock !== -1 && selectedVariant.stock <= 0)
                    }
                    style={{
                      backgroundColor: '#2557ff',
                      borderColor: '#2557ff',
                      height: '50px',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      borderRadius: '8px',
                      width: '100%'
                    }}
                  >
                    Add to My Products {selectedVariant && selectedVariant.stock !== -1 && selectedVariant.stock <= 0 ? '(Out of Stock)' : ''}
                  </Button>
                </Col>
                <Col span={10}>
                  <Button
                    size="large"
                    onClick={handleOrderSamples}
                    style={{
                      height: '50px',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      borderRadius: '8px',
                      width: '100%',
                      borderColor: '#2557ff',
                      color: '#2557ff'
                    }}
                    disabled={!selectedVariant || (selectedVariant.stock !== -1 && selectedVariant.stock <= 0)}
                  >
                    Order Samples
                  </Button>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </div>

      {/* Footer */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 24px' }}>
        <Footer />
      </div>
    </div>
  </>
  );
};

export default ProductDetailPage;
